import React from 'react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { Notifications } from '@mui/icons-material';
import { updateFCMToken } from '../../services/fcmService';
import { setupForegroundMessageListener } from '../../services/firebase';

/**
 * Toast notification styles
 */
const TOAST_STYLES = {
    container: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '4px 0'
    },
    icon: {
        color: '#fd7000',
        fontSize: '24px',
        marginTop: '2px',
        flexShrink: 0
    },
    content: {
        flex: 1
    },
    title: {
        fontWeight: 600,
        fontSize: '16px',
        color: '#fff',
        marginBottom: '4px',
        lineHeight: '1.3'
    },
    body: {
        fontSize: '14px',
        color: 'rgba(255, 255, 255, 0.8)',
        lineHeight: '1.4',
        wordWrap: 'break-word'
    },
    image: {
        maxWidth: '100%',
        maxHeight: '150px',
        borderRadius: '8px',
        marginTop: '8px',
        objectFit: 'cover'
    },
    toastOptions: {
        position: 'top-right',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: {
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
            border: '1px solid rgba(253, 112, 0, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 32px rgba(253, 112, 0, 0.2)',
            maxWidth: '400px',
            backdropFilter: 'blur(10px)'
        }
    }
};

/**
 * Render notification toast content
 */
const renderNotificationContent = (notification) => {
    return (
        <div style={TOAST_STYLES.container}>
            <Notifications style={TOAST_STYLES.icon} />
            <div style={TOAST_STYLES.content}>
                {notification?.title && (
                    <div style={TOAST_STYLES.title}>
                        {notification.title}
                    </div>
                )}
                {notification?.body && (
                    <div style={TOAST_STYLES.body}>
                        {notification.body}
                    </div>
                )}
                {notification?.image && (
                    <img
                        src={notification.image}
                        alt="Notification"
                        style={TOAST_STYLES.image}
                    />
                )}
            </div>
        </div>
    );
};

/**
 * Custom hook for FCM notifications
 * Handles FCM token updates and foreground message notifications
 * @param {boolean} isAuthenticated - User authentication status
 */
export const useFCMNotifications = (isAuthenticated) => {
    // Update FCM token when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            updateFCMToken().catch(error => {
                console.error('Error updating FCM token:', error);
            });
        }
    }, [isAuthenticated]);

    // Setup foreground message listener
    useEffect(() => {
        if (!isAuthenticated) return;

        const handleForegroundMessage = (payload) => {
            const notification = payload.notification;
            const data = payload.data || {};

            toast(
                renderNotificationContent(notification),
                {
                    ...TOAST_STYLES.toastOptions,
                    autoClose: data.autoClose ? parseInt(data.autoClose) : 5000,
                    onClick: () => {
                        if (data.clickAction) {
                            window.location.href = data.clickAction;
                        }
                    }
                }
            );
        };

        const cleanup = setupForegroundMessageListener(handleForegroundMessage);
        return cleanup;
    }, [isAuthenticated]);
};

